// DTO → view model for rent receipts.
//
// Two invariants this file exists to hold:
//   1. Money stays a STRING. Never Number() a money field here — this screen
//      feeds what investors are eventually paid (ux-principles §9).
//   2. Lists always emit PaginatedResponse<T>, with totalPages synthesized when
//      the wire omits it, so no container does pagination maths.

import type { PaginatedResponse } from '@shared/types/pagination'
import type {
  PreviewLayer1DeductionLineDto,
  PreviewRentReceiptDto,
  RentReceiptCountsDto,
  RentReceiptDeductionDto,
  RentReceiptDetailDto,
  RentReceiptDto,
  RentReceiptsListDto,
  RentRecognitionSliceDto,
  ReconciliationResultDto,
  RentReceiptApprovalPermissionDto,
} from './rentReceiptsContracts'
import type {
  PreviewLayer1DeductionLine,
  PreviewRentReceipt,
  ReconciliationResult,
  RentReceipt,
  RentReceiptCounts,
  RentReceiptDeduction,
  RentReceiptDetail,
  RentRecognitionSlice,
  RentReceiptApprovalPermission,
} from '../types/rentReceiptTypes'

const ZERO = '0.00'
const DEFAULT_PAGE = 1
const DEFAULT_LIMIT = 20

/** Money default — keeps every amount a string even when the wire omits it. */
function money(value: string | null | undefined): string {
  return value ?? ZERO
}

export function parseRentReceipt(dto: RentReceiptDto): RentReceipt {
  return {
    id: dto.id,
    receiptReference: dto.receiptReference,
    propertyId: dto.propertyId,
    propertyName: dto.propertyName ?? null,
    propertyReferenceCode: dto.propertyReferenceCode ?? null,
    receiptType: dto.receiptType,
    dateReceived: dto.dateReceived,
    periodCoveredFrom: dto.periodCoveredFrom,
    periodCoveredTo: dto.periodCoveredTo,
    declaredGrossRent: money(dto.declaredGrossRent),
    amountReceived: money(dto.amountReceived),
    // Deliberately NOT defaulted, for the same reason as varianceAmount below:
    // null means "no lease figure on record", which an operator has to be able
    // to tell apart from a property that genuinely brings in nothing.
    monthlyGrossRent: dto.monthlyGrossRent ?? null,
    monthlyNetRent: dto.monthlyNetRent ?? null,
    currencyCode: dto.currencyCode ?? 'AED',
    status: dto.status,
    reconciliationOutcome: dto.reconciliationOutcome ?? null,
    // Deliberately NOT defaulted to '0.00': null means "not reconciled yet",
    // which reads differently from a reconciled receipt that matched exactly.
    varianceAmount: dto.varianceAmount ?? null,
    deductionTotal: money(dto.deductionTotal),
    createdByName: dto.createdByName ?? null,
    approvedByName: dto.approvedByName ?? null,
    createdAt: dto.createdAt,
  }
}

export function parseRentReceiptDeduction(dto: RentReceiptDeductionDto): RentReceiptDeduction {
  return {
    id: dto.id,
    sequence: dto.sequence,
    deductionType: dto.deductionType,
    otherDescription: dto.otherDescription ?? null,
    amount: money(dto.amount),
    invoiceDocumentId: dto.invoiceDocumentId ?? null,
    exceedsThreshold: dto.exceedsThreshold ?? false,
    thresholdAcknowledgedAt: dto.thresholdAcknowledgedAt ?? null,
    source: dto.source,
  }
}

export function parseRentRecognitionSlice(dto: RentRecognitionSliceDto): RentRecognitionSlice {
  return {
    id: dto.id,
    periodKey: dto.periodKey,
    periodStart: dto.periodStart,
    periodEnd: dto.periodEnd,
    scheduledAmount: money(dto.scheduledAmount),
    releasedAmount: money(dto.releasedAmount),
    status: dto.status,
    distributionId: dto.distributionId ?? null,
  }
}

export function parseRentReceiptDetail(dto: RentReceiptDetailDto): RentReceiptDetail {
  return {
    ...parseRentReceipt(dto),
    chequeClearanceDate: dto.chequeClearanceDate ?? null,
    bankReference: dto.bankReference ?? null,
    varianceReason: dto.varianceReason ?? null,
    reconciledAt: dto.reconciledAt ?? null,
    approvedAt: dto.approvedAt ?? null,
    rejectedReason: dto.rejectedReason ?? null,
    statementDocumentId: dto.statementDocumentId ?? null,
    notes: dto.notes ?? null,
    correctsReceiptId: dto.correctsReceiptId ?? null,
    deductions: (dto.deductions ?? []).map(parseRentReceiptDeduction),
    recognitionSchedule: (dto.recognitionSchedule ?? []).map(parseRentRecognitionSlice),
  }
}

export function parseRentReceiptsList(dto: RentReceiptsListDto): PaginatedResponse<RentReceipt> {
  const rows = dto.data ?? []
  const limit = dto.meta?.limit ?? DEFAULT_LIMIT
  const totalItems = dto.meta?.total ?? rows.length

  return {
    data: rows.map(parseRentReceipt),
    pagination: {
      page: dto.meta?.page ?? DEFAULT_PAGE,
      limit,
      totalItems,
      totalPages: dto.meta?.totalPages ?? (limit > 0 ? Math.ceil(totalItems / limit) : 0),
    },
  }
}

export function parseRentReceiptCounts(dto: RentReceiptCountsDto): RentReceiptCounts {
  const variance = dto.variance ?? 0
  const disputed = dto.disputed ?? 0

  return {
    draft: dto.draft ?? 0,
    pendingReconciliation: dto.pendingReconciliation ?? 0,
    matched: dto.matched ?? 0,
    variance,
    disputed,
    pendingApproval: dto.pendingApproval ?? 0,
    approved: dto.approved ?? 0,
    used: dto.used ?? 0,
    corrected: dto.corrected ?? 0,
    total: dto.total ?? 0,
    // Recompute rather than trust the wire, so the badge and the tab counts
    // can never disagree on screen.
    needsAttention: dto.needsAttention ?? variance + disputed,
  }
}

export function parseReconciliationResult(dto: ReconciliationResultDto): ReconciliationResult {
  return {
    outcome: dto.outcome,
    varianceAmount: money(dto.varianceAmount),
    expectedRemittance: money(dto.expectedRemittance),
  }
}

function parsePreviewLine(dto: PreviewLayer1DeductionLineDto): PreviewLayer1DeductionLine {
  return {
    sequence: dto.sequence,
    deductionType: dto.deductionType,
    amount: money(dto.amount),
    source: dto.source,
  }
}

export function parsePreviewRentReceipt(dto: PreviewRentReceiptDto): PreviewRentReceipt {
  return {
    lines: (dto.lines ?? []).map(parsePreviewLine),
    total: money(dto.total),
    netExpectedRent: money(dto.netExpectedRent),
  }
}

/** Fail-closed default, same shape as the distribution-run permission parsers. */
export function parseRentReceiptApprovalPermission(
  dto: RentReceiptApprovalPermissionDto,
): RentReceiptApprovalPermission {
  return {
    canApprove: dto.canApprove ?? false,
    blockedReason: dto.blockedReason ?? null,
  }
}
